import {
  Alert,
  Button,
  Card,
  CardHeader,
  CardBody,
  Form,
  Input,
  Link,
} from '@heroui/react';
import _ from 'lodash';
import { Navigate } from 'react-router';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Logo from '@/assets/images/seamlessMD-logo.png';
import { FormFieldSecure } from '@/components/layout/Elements';
import { LangSelector } from '@/components/layout/LangSelector';
import useHospitalFlags from '@/hooks/useHospitalFlags';
import { useLoginMutation } from '@/patient-app-common/api/authApi';
import { useAppSelector } from '@/redux/hooks';
import { setRoute } from '@/patient-app-common/slices/appSlice';
import { validateTestServer } from '@/patient-app-common/utils';
import { getSeasonalLoginImage } from '@/utils/seasonalImages';

import { ReCaptcha } from './layout/ReCaptcha';
import styles from './styles/login.module.css';

export function Login() {
  const [login, { isLoading }] = useLoginMutation();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation('login');

  const { token } = useAppSelector(({ auth }) => auth);
  const { testEnv } = useAppSelector(({ app }) => app);

  const { hasLangSelector, hasOntarioTrustedAccount, hasSignup } =
    useHospitalFlags();

  const [isHuman, setIsHuman] = useState(true);
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [loginError, setLoginError] = useState('');
  const [testServer, setTestServer] = useState('ca-canary-3');

  const [form, setForm] = useState({
    email: '',
    password: '',
  });

  useEffect(() => {
    if (!testEnv || !testServer) return;

    const route = validateTestServer(testServer);

    if (route) {
      dispatch(setRoute(route));
    }
  }, [testEnv, testServer]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setLoginError('');
    setErrorFields([]);
    const { name, value } = e.target;
    setForm((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateInput = () => {
    setErrorFields([]);
    const fields = [];

    if (!form.email.length) {
      fields.push('email');
    }

    if (!form.password.length) {
      fields.push('password');
    }

    if (fields.length) {
      setErrorFields(fields);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const valid = validateInput();

    if (!valid) {
      return;
    }

    const params = {
      username: form.email,
      pin: form.password,
    };

    if (!isHuman) {
      setLoginError(t('common.error.recaptcha'));
      return;
    }

    setLoginError('');

    try {
      await login(params).unwrap();
    } catch (error: unknown) {
      if (_.has(error, 'status') && error.status === 404) {
        setLoginError(t('login.error.unauthorized'));
      } else {
        setLoginError(t('common.error.server'));
      }
    }
  };

  if (token) {
    return <Navigate to="/" replace />;
  }

  const seasonalBackground = getSeasonalLoginImage();

  return (
    <div
      style={{ backgroundImage: `url(${seasonalBackground})` }}
      className={styles.background}
    >
      <div className="h-[100dvh]">
        <section className={styles.container}>
          <div className={styles.languageSelector}>
            <LangSelector visible={hasLangSelector} />
          </div>
          <HospitalLogo />
          <div className={styles.formContainer}>
            <Card shadow="sm" className="border border-grey px-4 mx-2">
              <CardHeader className="flex-col">
                <OntarioTrustLogin visible={hasOntarioTrustedAccount} />
                <h1 className="text-xl font-semibold">
                  {t('login.form.title')}
                </h1>
              </CardHeader>
              <CardBody className="gap-4">
                <Alert
                  color="danger"
                  hideIcon
                  isClosable
                  isVisible={!_.isEmpty(loginError)}
                  onClose={() => setLoginError('')}
                  radius="none"
                  title={loginError}
                />
                <Form onSubmit={handleSubmit} className="gap-8">
                  <Input
                    autoComplete="off"
                    color="primary"
                    errorMessage={t('login.error.blank_user')}
                    isInvalid={errorFields.includes('email')}
                    label={t('login.input.user')}
                    name="email"
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleInputChange(e)
                    }
                    value={form.email}
                    variant="bordered"
                  />
                  <FormFieldSecure
                    color="primary"
                    errorMessage={t('login.error.blank_password')}
                    isInvalid={errorFields.includes('password')}
                    label={t('common.input.password')}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleInputChange(e)
                    }
                    value={form.password}
                    variant="bordered"
                  />
                  {testEnv && (
                    <Input
                      autoComplete="off"
                      color="primary"
                      isInvalid={errorFields.includes('email')}
                      label="Server"
                      name="server"
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setTestServer(e.target.value)
                      }
                      value={testServer}
                      variant="bordered"
                    />
                  )}
                  <ReCaptcha
                    setIsHuman={(result: boolean) => setIsHuman(result)}
                  />
                  <Button
                    className="w-full"
                    color="primary"
                    isLoading={isLoading}
                    type="submit"
                  >
                    {t('common.action.login')}
                  </Button>
                </Form>
                {hasSignup && (
                  <Button
                    as={Link}
                    color="primary"
                    href="/signup"
                    variant="bordered"
                  >
                    {t('common.link.signup')}
                  </Button>
                )}
                <div className={styles.linksContainer}>
                  <Link href="/reset_password_form">
                    {t('common.link.help_account')}
                  </Link>
                  <Link
                    href={`https://ca.seamless.md/education/241780?lang=${i18n.language}`}
                    isExternal
                  >
                    {t('common.link.help')}
                  </Link>
                </div>
              </CardBody>
            </Card>
          </div>
        </section>
      </div>
    </div>
  );
}

function HospitalLogo() {
  const { hospital, hasOntarioTrustedAccount } = useHospitalFlags();

  if (hospital?.logo && hasOntarioTrustedAccount) {
    return (
      <Card className="bg-white mb-4 px-2">
        <div className="flex flex-col sm:flex-row justify-center place-items-center">
          <img
            style={{ height: '80px', maxWidth: '200px' }}
            alt={hospital.displayName}
            src={hospital.logo}
          />
          <img
            src="/icons/moh.png"
            alt="Government of Ontario"
            style={{ height: '80px' }}
          />
        </div>
      </Card>
    );
  }

  if (hospital?.logo) {
    return (
      <img
        className={styles.logoContainer}
        src={hospital.logo}
        alt={hospital.displayName}
      />
    );
  }

  return <img className={styles.logoContainer} src={Logo} alt="SeamlessMD" />;
}

function OntarioTrustLogin({ visible }: { visible: boolean }) {
  const { route } = useAppSelector(({ app }) => app);

  if (!visible) return null;

  return (
    <div>
      <Button
        as={Link}
        className="border-black mt-4 w-full"
        href={`${route}/integration/identos/niagarahealthnavigator/start`}
        variant="bordered"
      >
        Login with your Ontario trusted account
      </Button>
      <div className="p-4 text-center">
        <p className="text-default-500 text-sm">
          Your consent is required to continue
        </p>
      </div>
      <div className="flex items-center mb-4">
        <div className="flex-grow bg bg-gray-300 h-[1px]"></div>
        <div className="flex-grow-0 mx-4 text-default-500 text-sm">or</div>
        <div className="flex-grow bg bg-gray-300 h-[1px]"></div>
      </div>
    </div>
  );
}
